import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiGetMe } from '../auth';
import {
  Box, Sheet, Typography, Button, Alert, Card, CardContent, Grid,
  Modal, ModalDialog, ModalClose, List, ListItemButton, ListItemContent,
  ListDivider, Avatar, CircularProgress, Chip, Divider, Snackbar, Badge,
  Input, Stack, IconButton, useTheme,
} from '@mui/joy';
import useMediaQuery from '@mui/material/useMediaQuery';

import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { LayoutContext } from '../context';
import {
  APIGetUnreadCounts, APIGetPatientInfo, APIGetSpecializations, APIGetDoctors, APIDutyStart,
  APICreateConnectionRequest, APIConnectByToken, APIGetChatSettings
} from '../api';
import MessagesPane from '../components/MessagesChat/MessagesPane';
import BookingModal from '../components/booking/BookingModal';
import MyAppointmentsCard from '../components/booking/MyAppointmentsCard';
import ReviewModal from '../components/review/ReviewModal';
import { RevealGroup, Reveal } from '../components/ui/Reveal';
import PushNotificationButton from '../components/PushNotificationButton';
import ModalSelectDoctor from '../components/modals/ModalSelectDoctor';


// --- helpers ---------------------------------------------------------------

// Время последнего сообщения в коротком виде: время / вчера / дд.мм
function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (sameDay) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (isYesterday) return 'вчера';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

// Специализация собеседника из участников чата (если есть)
function chatSpecText(chat) {
  const peers = chat?.participants || [];
  for (const p of peers) {
    const spec = p?.profile?.specialization;
    if (Array.isArray(spec) && spec.length) {
      return spec.map((s) => s.name || s).join(', ');
    }
  }
  return '';
}

// Рендер HTML из BotText (теги <b>/<i>/<a>); ссылки открываем в новой вкладке.
// При желании ужесточить — добавить DOMPurify (npm i dompurify) и прогнать через него.
function htmlToProps(html) {
  const safe = (html || '').replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
  return { __html: safe };
}

// --- component -------------------------------------------------------------

export default function PatientCabinetPage() {
  const navigate = useNavigate();
  const {
    account, token, registrationStatus, isApproved, logout, applyLogin,
    chats, loadChats, loadsUnreadCounts, setChats, unreadCounts,
  } = useContext(LayoutContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const approved = isApproved ?? (registrationStatus === 'approved');
  useEffect(() => {
    if (approved || !account?.id || !token) return;
    let disposed = false;
    let busy = false;
    const owner = account.id;
    const refresh = async () => {
      if (busy || document.visibilityState !== 'visible') return;
      busy = true;
      try {
        const result = await apiGetMe();
        if (!disposed && result.profile?.id === owner && result.profile.registration_status !== registrationStatus)
          applyLogin(result.profile);
      } catch { /* Существующий auth interceptor обрабатывает истёкшую сессию. */ }
      finally { busy = false; }
    };
    const interval = setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => { disposed = true; clearInterval(interval); document.removeEventListener('visibilitychange', refresh); window.removeEventListener('focus', refresh); };
  }, [approved, account?.id, token, registrationStatus]);

  // Чаты
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [unreadMap, setUnreadMap] = useState({}); // { [chat_id]: count }

  // Инфо-разделы (как нас найти / FAQ) + контакты
  const [info, setInfo] = useState(null);
  const [infoModal, setInfoModal] = useState(null); // null | 'howToFind' | 'faq'

  const [snack, setSnack] = useState({ open: false, color: 'success', msg: '' });

  // Онлайн-запись (бронь через МИС)
  const [bookingOpen, setBookingOpen] = useState(false);
  const [apptRefreshKey, setApptRefreshKey] = useState(0);

  // Оценка приёма
  const [reviewOpen, setReviewOpen] = useState(false);

  // --- дежурная часть ---
  const [dutyLoading, setDutyLoading] = useState(false);

  // --- выбор специалиста (заявка на подключение) ---
  const hasTariff = !!account?.has_active_tariff;
  const [docSelectOpen, setDocSelectOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  // Фиче-флаг «Подключиться к специалисту» (из /chat-settings/). По умолчанию скрыто.
  const [specialistConnectEnabled, setSpecialistConnectEnabled] = useState(false);

  const onSelectDoctor = async (doctor) => {
    if (!window.confirm(`Отправить запрос на подключение к врачу ${doctor.name}?`)) return;
    setRequesting(true);
    try {
      const resp = await APICreateConnectionRequest(doctor.id, token);
      setDocSelectOpen(false);
      setSnack({ open: true, color: resp?.already ? 'neutral' : 'success', msg: resp?.message || 'Запрос отправлен' });
    } catch (e) {
      setSnack({ open: true, color: 'danger', msg: e?.response?.data?.error || 'Не удалось отправить запрос' });
    } finally {
      setRequesting(false);
    }
  };

  // --- загрузка непрочитанного и инфо при монтировании ---
  const refreshUnread = async () => {
    try {
      const data = await APIGetUnreadCounts(token);
      const map = {};
      (data?.unread_counts || []).forEach((u) => { map[u.chat_id] = u.unread_count; });
      setUnreadMap(map);
    } catch (e) { /* тихо */ }
  };

  useEffect(() => {
    if (!token) return;
    loadChats?.();
    refreshUnread();
    APIGetPatientInfo(token).then(setInfo).catch(() => {});
    APIGetChatSettings(token)
      .then((s) => setSpecialistConnectEnabled(!!s?.specialist_connect_enabled))
      .catch(() => {});

    // Отложенное подключение по ссылке врача (если пациент пришёл по /connect и затем вошёл)
    const pendingDoctor = localStorage.getItem('pendingDoctorToken');
    if (pendingDoctor) {
      APIConnectByToken(pendingDoctor, token)
        .then((r) => {
          setSnack({ open: true, color: 'success', msg: r?.message || 'Вы подключены к врачу' });
          loadChats?.();
        })
        .catch(() => {})
        .finally(() => localStorage.removeItem('pendingDoctorToken'));
    }
  }, [token]);

  // Когда чат сбрасывается (в т.ч. кнопкой «назад» внутри MessagesPane) — закрываем модалку
  useEffect(() => { if (!selectedChat) setChatModalOpen(false); }, [selectedChat]);

  // Realtime: как только контекст обновил непрочитанные (WS/пуш) — пересобираем карту бейджей (баг #9)
  useEffect(() => {
    if (!unreadCounts?.timestamp) return; // ещё не загружено — не трогаем локальную карту
    const map = {};
    (unreadCounts.unread_counts || []).forEach((u) => { map[u.chat_id] = u.unread_count; });
    setUnreadMap(map);
  }, [unreadCounts]);

  // --- чат ---
  const openChat = (chat) => {
    // Оптимистично гасим бейдж непрочитанного — сообщения будут отмечены
    // прочитанными при открытии (см. onMessagesRead у MessagesPane).
    setUnreadMap((m) => ({ ...m, [chat.chat_id]: 0 }));
    setSelectedChat(chat);
    setChatModalOpen(true);
  };

  const closeChat = () => {
    setSelectedChat(null);
    setChatModalOpen(false);
    loadChats?.();
    refreshUnread();
    loadsUnreadCounts?.();
  };

  // --- дежурная часть ---
  const openDuty = async () => {
    setDutyLoading(true);
    try {
      const resp = await APIDutyStart(token);
      const data = await loadChats?.();
      const list = data?.chats || [];
      const chat = list.find((c) => c.chat_id === resp.chat_id)
        || { chat_id: resp.chat_id, display_name: 'Дежурный врач', type_chat: 'duty' };
      openChat(chat);
    } catch (e) {
      setSnack({ open: true, color: 'danger', msg: e?.response?.data?.error || 'Не удалось открыть чат с дежурным врачом' });
    } finally {
      setDutyLoading(false);
    }
  };


  // --- внешние ссылки ---
  const openExternal = (url) => { if (url) window.open(url, '_blank', 'noopener,noreferrer'); };

  // --- UI: карточка-действие ---
  const ActionCard = ({ icon, title, subtitle, onClick, color = 'primary', disabled }) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%', opacity: disabled ? 0.5 : 1, transition: 'transform .15s, box-shadow .15s',
        '&:hover': disabled ? {} : { transform: 'translateY(-2px)', boxShadow: 'md', cursor: 'pointer' },
      }}
      onClick={disabled ? undefined : onClick}
    >
      <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Avatar variant="soft" color={color} sx={{ '--Avatar-size': '40px' }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography level="title-md">{title}</Typography>
          {subtitle && (
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mt: 0.25 }}>{subtitle}</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', p: { xs: 2, sm: 3 }, width: '100%' }}>
      <RevealGroup>
      {/* Шапка профиля */}
      <Reveal>
      <Box sx={{ mb: 2 }}>
        <PushNotificationButton />
      </Box>
      <Sheet
        variant="soft" color="primary"
        sx={{
          p: 2.5, borderRadius: 'xl', mb: 2, display: 'flex', alignItems: 'center', gap: 2,
          background: 'linear-gradient(135deg, var(--joy-palette-primary-100), var(--joy-palette-primary-50))',
          border: '1px solid', borderColor: 'primary.100',
          boxShadow: '0 10px 30px -18px rgba(64,61,132,0.55)',
        }}
      >
        <Avatar size="lg">{(account?.name || 'П').slice(0, 1)}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-lg">{account?.name || 'Пациент'}</Typography>
          <Typography level="body-sm" textColor="text.tertiary">{account?.phone || ''}</Typography>
        </Box>
        <Chip color={approved ? 'success' : 'warning'} variant="solid">
          {approved ? 'Активен' : 'На рассмотрении'}
        </Chip>
      </Sheet>
      </Reveal>

      {/* === Статус тарифа: скрыт вместе со связью со спецом (пока кнопка не подключена) === */}
      {approved && specialistConnectEnabled && (
        <Reveal>
        <Sheet variant="soft" color={hasTariff ? 'success' : 'warning'}
          sx={{ p: 1.5, borderRadius: 'lg', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarRoundedIcon fontSize="small" />
          <Typography level="body-sm">
            {hasTariff
              ? `Тариф активен${account?.tariff_days_left ? `, осталось дней: ${account.tariff_days_left}` : ''}${account?.tariff_end_date ? ` — доступ по ${new Date(account.tariff_end_date + 'T00:00:00').toLocaleDateString('ru-RU')} включительно` : ''}`
              : 'Тариф не активен — переписка со специалистом недоступна. Свяжитесь с администратором для оплаты.'}
          </Typography>
        </Sheet>
        </Reveal>
      )}

      {!approved && (
        <Reveal>
        <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
          {account?.mis_review_required
            ? 'На ваш контакт найдено несколько карточек пациентов. Заявка передана администратору: он уточнит данные и подключит нужную карточку. Пока доступны возможности кабинета в режиме ожидания и дежурный чат.'
            : 'Ваша заявка на рассмотрении. Пока вы можете написать дежурному врачу — он ответит на ваши вопросы.'}
        </Alert>
        </Reveal>
      )}

      {/* === Чат с врачом: карточка со списком чатов (видна всем, в т.ч. на рассмотрении — для дежурной части) === */}
      {(
        <Reveal>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Avatar variant="soft" color="primary" sx={{ '--Avatar-size': '40px' }}>
                <ChatRoundedIcon />
              </Avatar>
              <Box>
                <Typography level="title-md">Чат с врачом</Typography>
                <Typography level="body-sm" textColor="text.tertiary">Переписка с вашим специалистом</Typography>
              </Box>
            </Box>

            {(!chats || chats.length === 0) ? (
              <Alert variant="soft" color="neutral" sx={{ mt: 1 }}>
                У вас пока нет чатов. Напишите дежурному врачу, чтобы начать общение.
              </Alert>
            ) : (
              <List sx={{ '--ListItem-paddingY': '10px', mt: 1 }}>
                {chats.map((chat, idx) => {
                  const unread = unreadMap[chat.chat_id] || 0;
                  const spec = chatSpecText(chat);
                  return (
                    <Box key={chat.chat_id}>
                      {idx > 0 && <ListDivider />}
                      <ListItemButton onClick={() => openChat(chat)} sx={{ borderRadius: 'sm' }}>
                        <Badge badgeContent={unread} color="danger" max={99} invisible={!unread}>
                          <Avatar size="sm">{(chat.display_name || '?').slice(0, 1)}</Avatar>
                        </Badge>
                        <ListItemContent sx={{ ml: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography level="title-sm" noWrap>{chat.type_chat === 'duty' ? 'Дежурный врач' : (chat.display_name || 'Чат')}</Typography>
                            <Typography level="body-xs" textColor="text.tertiary">
                              {formatTime(chat.last_message?.date_create)}
                            </Typography>
                          </Box>
                          {chat.type_chat === 'duty'
                            ? <Typography level="body-xs" textColor="warning.plainColor" noWrap>Дежурные врачи клиники</Typography>
                            : (spec && <Typography level="body-xs" textColor="text.tertiary" noWrap>{spec}</Typography>)}
                          <Typography level="body-sm" textColor="text.tertiary" noWrap>
                            {chat.last_message?.content || 'Нет сообщений'}
                          </Typography>
                          {chat.can_write === false && chat.write_block_message
                            && chat.write_block_reason !== 'tariff_expired' && (
                            <Typography level="body-xs" textColor="danger.plainColor" noWrap>
                              {chat.write_block_message}
                            </Typography>
                          )}
                        </ListItemContent>
                      </ListItemButton>
                    </Box>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>
        </Reveal>
      )}

      {/* === Написать дежурному врачу (доступно всем: и подтверждённым, и на рассмотрении) === */}
      <Reveal>
      <Box sx={{ mb: 2 }}>
        <ActionCard
          icon={<SupportAgentRoundedIcon />}
          color="warning"
          title="Написать дежурному врачу"
          subtitle="Дежурный специалист ответит на ваш вопрос"
          onClick={openDuty}
          disabled={dutyLoading}
        />
      </Box>
      </Reveal>

      {/* === Подключиться к специалисту: скрыто фиче-флагом specialist_connect_enabled (правка №3) === */}
      {approved && specialistConnectEnabled && (
        <Reveal>
        <Box sx={{ mb: 2 }}>
          <ActionCard
            icon={<PersonSearchRoundedIcon />}
            title="Подключиться к специалисту"
            subtitle="Выбрать врача и отправить запрос на подключение"
            onClick={() => setDocSelectOpen(true)}
          />
        </Box>
        </Reveal>
      )}

      {/* === Мои записи (из МИС) === */}
      {approved && (
        <Reveal>
        <MyAppointmentsCard
          token={token}
          refreshKey={apptRefreshKey}
          onChanged={() => setSnack({ open: true, color: 'success', msg: 'Запись отменена' })}
        />
        </Reveal>
      )}

      {/* === Группа кнопок: запись / как найти === */}
      <Reveal>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
        <Button
          fullWidth size="lg" variant="solid" color="primary"
          startDecorator={<EventAvailableRoundedIcon />}
          onClick={() => setBookingOpen(true)}
        >
          Записаться на приём
        </Button>
        <Button
          fullWidth size="lg" variant="soft" color="primary"
          startDecorator={<PlaceRoundedIcon />}
          onClick={() => setInfoModal('howToFind')}
        >
          Как нас найти
        </Button>
      </Stack>
      </Reveal>

      {/* === Вспомогательные действия === */}
      <Reveal>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
        <Button
          fullWidth variant="soft" color="neutral"
          startDecorator={<SupportAgentRoundedIcon />}
          endDecorator={<OpenInNewRoundedIcon />}
          onClick={() => openExternal(info?.admin_contact?.url)}
          disabled={!info?.admin_contact?.url}
        >
          Написать администратору
        </Button>
        <Button
          fullWidth variant="soft" color="neutral"
          startDecorator={<HelpRoundedIcon />}
          onClick={() => setInfoModal('faq')}
        >
          Частые вопросы
        </Button>
        <Button
          fullWidth variant="soft" color="neutral"
          startDecorator={<StarRoundedIcon />}
          onClick={() => setReviewOpen(true)}
        >
          Оценить чат с доктором
        </Button>
      </Stack>
      </Reveal>

      <Reveal><Divider sx={{ my: 3 }} /></Reveal>

      <Reveal>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button variant="outlined" color="neutral" startDecorator={<DevicesRoundedIcon />} onClick={() => navigate('/sessions')}>
          Устройства и сеансы
        </Button>
        <Button
          variant="outlined" color="danger" startDecorator={<LogoutRoundedIcon />}
          onClick={() => { logout(); navigate('/login/patient', { replace: true }); }}
        >
          Выйти
        </Button>
      </Box>
      </Reveal>
      </RevealGroup>

      {/* === Модалка чата (реальное время через MessagesPane) === */}
      <Modal open={chatModalOpen} onClose={closeChat} sx={{ zIndex: 10012 }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(20, 22, 35, 0.38)', backdropFilter: 'blur(3px)' } } }}>
        <ModalDialog
          layout={isMobile ? 'fullscreen' : 'center'}
          aria-label="Чат с врачом"
          sx={{
            p: 0, gap: 0, overflow: 'hidden', minHeight: 0,
            width: isMobile ? '100vw' : 'min(820px, calc(100vw - 48px))',
            height: isMobile ? '100dvh' : 'min(88dvh, 900px)',
            maxHeight: 'none', maxWidth: 'none',
            borderRadius: isMobile ? 0 : '24px',
            border: '1px solid', borderColor: 'divider', bgcolor: 'background.surface',
            boxShadow: '0 24px 80px rgba(25, 28, 45, 0.22)',
          }}
        >
          {selectedChat && (
            <MessagesPane
              chat={selectedChat}
              chats={chats}
              setChats={setChats}
              searchParams={new URLSearchParams()}
              setSearchParams={() => {}}
              setSelectedChat={setSelectedChat}
              heightOverride="100%"
              onCloseChat={closeChat}
              onMessagesRead={(chatId) => setUnreadMap((m) => ({ ...m, [chatId]: 0 }))}
            />
          )}
        </ModalDialog>
      </Modal>

      {/* === Инфо-модалка: как нас найти / FAQ === */}
      <Modal open={infoModal != null} onClose={() => setInfoModal(null)}>
        <ModalDialog
          layout={isMobile ? 'fullscreen' : 'center'}
          sx={isMobile ? {} : { maxWidth: 560, width: '100%' }}
        >
          <ModalClose />
          {infoModal === 'howToFind' && (
            <>
              <Typography level="title-lg" sx={{ mb: 1 }}>Как нас найти</Typography>
              {info?.how_to_find?.intro && (
                <Box sx={{ whiteSpace: 'pre-wrap', mb: 1, '& a': { color: 'var(--joy-palette-primary-600)' } }}
                     dangerouslySetInnerHTML={htmlToProps(info.how_to_find.intro)} />
              )}
              <Stack spacing={1.5} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                {(info?.how_to_find?.branches || []).map((b) => (
                  <Card key={b.key} variant="soft">
                    <CardContent>
                      <Box sx={{ whiteSpace: 'pre-wrap', '& a': { color: 'var(--joy-palette-primary-600)' } }}
                           dangerouslySetInnerHTML={htmlToProps(b.text)} />
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </>
          )}
          {infoModal === 'faq' && (
            <>
              <Typography level="title-lg" sx={{ mb: 1 }}>Частые вопросы</Typography>
              <Box sx={{ whiteSpace: 'pre-wrap', overflow: 'auto', '& a': { color: 'var(--joy-palette-primary-600)' } }}
                   dangerouslySetInnerHTML={htmlToProps(info?.faq?.text)} />
            </>
          )}
        </ModalDialog>
      </Modal>

      <ModalSelectDoctor
        open={docSelectOpen}
        onClose={() => setDocSelectOpen(false)}
        token={token}
        submitting={requesting}
        title="Выберите специалиста"
        onSelect={onSelectDoctor}
      />
      
      {/* === Модалка онлайн-записи === */}
      <BookingModal
        open={bookingOpen}
        onClose={() => {
          setBookingOpen(false);
          // Запись могла быть создана в виджете — обновляем «Мои записи».
          setApptRefreshKey((k) => k + 1);
        }}
        token={token}
      />

      {/* === Модалка оценки приёма === */}
      <ReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        token={token}
        onSubmitted={(resp) => setSnack({ open: true, color: 'success', msg: resp?.updated ? 'Ваша оценка обновлена' : 'Спасибо! Ваша оценка отправлена' })}
      />

      <Snackbar
        open={snack.open} color={snack.color} variant="soft" autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack.msg}
      </Snackbar>
    </Box>
  );
}